import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppBar, Badge, Button, Card, Icon, Skeleton } from '@astral/ui';
import {
  packsPerMonth,
  stickersPerPack,
  type Plan,
  STICKER_TYPES,
  type PaymentMethod,
  type PremiumPlanDTO,
  type PremiumStatusDTO,
} from '@astral/shared';
import { closeMiniApp, openTelegramLink } from '@astral/telegram/webapp';
import { PremiumStarsIcon, TomanPaymentIcon } from '../components/PaymentIcons.js';
import { api } from '../lib/api.js';
import { useI18n, useT, type TranslationKey } from '../providers/I18nProvider.js';

const LOCALE: Record<string, string> = { en: 'en-US', fa: 'fa-IR', ru: 'ru-RU' };
const BOT = (import.meta.env.VITE_BOT_USERNAME as string) || 'AstralStickerBot';
/** One short macrotask — long enough for the deep-link event to reach the
 *  native client, short enough to feel like a single tap. */
const HANDOFF_CLOSE_MS = 120;

function fmtDate(iso: string, language: string): string {
  try {
    return new Intl.DateTimeFormat(LOCALE[language] ?? 'en-US', { year: 'numeric', month: 'long', day: 'numeric' }).format(new Date(iso));
  } catch {
    return iso.slice(0, 10);
  }
}
function fmtToman(n: number, language: string): string {
  try {
    return new Intl.NumberFormat(LOCALE[language] ?? 'en-US').format(n);
  } catch {
    return String(n);
  }
}

/** Interactive Premium: pick plan → pick method → CTA. Purchase happens in the bot. */
/** The three entitlement levels, in the order the user should read them. */
const COMPARE_TIERS: { plan: Plan; labelKey: TranslationKey }[] = [
  { plan: 'FREE', labelKey: 'premium.planFree' },
  { plan: 'PREMIUM', labelKey: 'premium.planMonth1' },
  { plan: 'PREMIUM_PLUS', labelKey: 'premium.planMonth3' },
];

export function PremiumScreen(): JSX.Element {
  const t = useT();
  const { language } = useI18n();
  const navigate = useNavigate();
  const [status, setStatus] = useState<PremiumStatusDTO | null>(null);
  const [plans, setPlans] = useState<PremiumPlanDTO[]>([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [planId, setPlanId] = useState<string | null>(null);
  const [method, setMethod] = useState<PaymentMethod | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([api.premiumStatus(), api.premiumPlans()])
      .then(([s, p]) => {
        if (cancelled) return;
        setStatus(s);
        setPlans(p);
        if (p[0]) setPlanId(p[0].id); // preselect the first plan so the flow is obvious
      })
      .catch(() => !cancelled && setFailed(true))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, []);

  const monthKey = (m: number): TranslationKey => `premium.month${m}` as TranslationKey;
  const isActive = status?.isPremium ?? false;
  const selectedPlan = useMemo(() => plans.find((p) => p.id === planId) ?? null, [plans, planId]);

  /**
   * Hand the purchase off to the bot.
   *
   * Both methods route through the bot (see the deep-link payloads below): the
   * bot creates the payment request and, for Stars, sends the real XTR invoice.
   * The Mini App never calls `openInvoice` itself.
   *
   * Bot API 7.0+ stopped closing the Mini App on `openTelegramLink`, so the
   * chat would stay hidden behind the app and the user would have to swipe it
   * away by hand. Telegram exposes no `minimize()`, so `close()` is the closest
   * supported behaviour — the bot chat becomes visible immediately.
   *
   * Order matters: open FIRST, then close. Closing first can tear the webview
   * down before the link event is delivered. The close is deferred by one short
   * timeout so the `openTelegramLink` message is flushed to the native client
   * before the app goes away; doing both in the same tick races on iOS.
   */
  const start = (m: PaymentMethod): void => {
    if (!selectedPlan) return;
    const prefix = m === 'TELEGRAM_STARS' ? 'st' : 'tm';
    openTelegramLink(`https://t.me/${BOT}?start=${prefix}_${selectedPlan.id}`);
    // Outside Telegram (dev/browser) the link opened in a new tab and there is
    // nothing to close — `closeMiniApp` reports that and we simply skip it.
    window.setTimeout(() => {
      closeMiniApp();
    }, HANDOFF_CLOSE_MS);
  };

  return (
    <div className="app-root">
      <AppBar title={t('premium.title')} onBack={() => navigate(-1)} />
      <div className="screen">
        {loading ? (
          <div className="stack">
            <Skeleton height={96} radius={20} />
            <Skeleton height={72} radius={16} />
            <Skeleton height={72} radius={16} />
          </div>
        ) : failed ? (
          <Card padding="md">
            <p className="muted">{t('premium.loadFailed')}</p>
          </Card>
        ) : (
          <>
            {/* Status */}
            <Card padding="lg" className="stack border-glow premium-status">
              <div className="row-between">
                <span className="premium-status__brand">
                  <Icon name="sparkles" size={20} /> {t('premium.title')}
                </span>
                <Badge tone={isActive ? 'success' : 'neutral'}>{isActive ? t('premium.active') : t('premium.free')}</Badge>
              </div>
              <p className="premium-status__line">{isActive ? t('premium.statusActive') : t('premium.statusFree')}</p>
              {isActive && status?.premiumExpiresAt && (
                <p className="muted" style={{ fontSize: 13 }}>
                  {t('premium.expires', { date: fmtDate(status.premiumExpiresAt, language) })}
                  {status.daysRemaining != null ? ` · ${t('premium.daysLeft', { count: status.daysRemaining })}` : ''}
                </p>
              )}
              {status?.openRequest && (
                <div className="premium-pending">
                  <Icon name="bell" size={16} />
                  <span>{status.openRequest.status === 'RECEIPT_SUBMITTED' ? t('premium.pendingBody') : t('premium.awaitingBody')}</span>
                </div>
              )}
            </Card>

            {/* Plans — tappable, selected highlights */}
            <div className="premium-section">
              <span className="screen__section-title premium-heading">{t('premium.plansTitle')}</span>
              <div className="premium-plans">
                {plans.map((plan) => (
                  <button
                    key={plan.id}
                    className={`premium-plan${plan.bestValue ? ' premium-plan--best' : ''}${planId === plan.id ? ' premium-plan--selected' : ''}`}
                    aria-pressed={planId === plan.id}
                    onClick={() => setPlanId(plan.id)}
                  >
                    {plan.bestValue && <span className="premium-plan__ribbon">{t('premium.bestValue')}</span>}
                    <span className="premium-plan__months">{t(monthKey(plan.months))}</span>
                    <span className="premium-plan__price">
                      <PremiumStarsIcon size={14} />
                      {plan.prices.stars}
                    </span>
                    <span className="premium-plan__save">{fmtToman(plan.prices.toman, language)}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Payment method — revealed after a plan is selected */}
            {selectedPlan && (
              <div className="premium-section premium-methods-block">
                <span className="screen__section-title premium-heading">{t('premium.methodTitle')}</span>
                <div className="premium-methods">
                <button
                  className={`premium-method${method === 'TOMAN_MANUAL' ? ' premium-method--selected' : ''}`}
                  aria-pressed={method === 'TOMAN_MANUAL'}
                  onClick={() => setMethod('TOMAN_MANUAL')}
                >
                  <span className="premium-method__flag">
                    <TomanPaymentIcon size={22} />
                  </span>
                  <span className="premium-method__text">
                    <span className="premium-method__name">{t('premium.methodToman')}</span>
                    <span className="premium-method__sub">{t('premium.methodTomanSub')}</span>
                  </span>
                  <span className="premium-method__price">{fmtToman(selectedPlan.prices.toman, language)}</span>
                </button>
                <button
                  className={`premium-method${method === 'TELEGRAM_STARS' ? ' premium-method--selected' : ''}`}
                  aria-pressed={method === 'TELEGRAM_STARS'}
                  onClick={() => setMethod('TELEGRAM_STARS')}
                >
                  <span className="premium-method__flag">
                    <PremiumStarsIcon size={22} />
                  </span>
                  <span className="premium-method__text">
                    <span className="premium-method__name">{t('premium.methodStars')}</span>
                    <span className="premium-method__sub">{t('premium.methodStarsSub')}</span>
                  </span>
                  <span className="premium-method__price">
                    <PremiumStarsIcon size={14} />
                    {selectedPlan.prices.stars}
                  </span>
                </button>
                </div>
              </div>
            )}

            {/* CTA — depends on chosen method */}
            {selectedPlan && method && (
              <Button
                size="lg"
                fullWidth
                leftIcon={
                  method === 'TELEGRAM_STARS' ? <PremiumStarsIcon size={18} /> : <TomanPaymentIcon size={18} />
                }
                onClick={() => start(method)}
              >
                {t('premium.subscribe', {
                  price:
                    method === 'TELEGRAM_STARS'
                      ? `${selectedPlan.prices.stars} ${t('premium.starsUnit')}`
                      : t('premium.tomanAmount', { amount: fmtToman(selectedPlan.prices.toman, language) }),
                })}
              </Button>
            )}
            <p className="muted premium-note" style={{ fontSize: 12, textAlign: 'center' }}>{t('premium.purchaseNote')}</p>

            {/* What each tier actually grants — read from the policy layer, so
                the UI can never advertise a limit the backend would reject. */}
            <Card padding="md" className="stack premium-compare">
              <span className="screen__section-title premium-heading">{t('premium.compareTitle')}</span>
              {/* Stated once: the Mixed number is a combined image+video total. */}
              <span className="premium-compare__note">
                {t('stickerTypes.MIXED')} · {t('premium.mixedNote')}
              </span>
              {COMPARE_TIERS.map(({ plan, labelKey }) => {
                return (
                  <div key={plan} className="premium-tier">
                    <div className="premium-tier__head">
                      <span className="premium-tier__name">{t(labelKey)}</span>
                    </div>
                    <div className="premium-tier__rows">
                      {STICKER_TYPES.map((type) => {
                        const limit = packsPerMonth(plan, type);
                        return (
                          <div key={type} className="premium-tier__row">
                            <span className="premium-tier__mode">
                              {t(`stickerTypes.${type}` as TranslationKey)}
                            </span>
                            {/* Each fragment is its own element: no string
                                concatenation, so numbers never collide with
                                labels and RTL bidi stays predictable. */}
                            <span className="premium-tier__detail">
                              {limit !== null && (
                                <>
                                  <span>{t('premium.packsPerMonth', { count: limit })}</span>
                                  <span className="premium-tier__sep" aria-hidden="true" />
                                </>
                              )}
                              <span>
                                {type === 'MIXED'
                                  ? t('premium.perPackTotal', { count: stickersPerPack(plan, type) })
                                  : t('premium.perPack', { count: stickersPerPack(plan, type) })}
                              </span>
                            </span>
                          </div>
                        );
                      })}
                      {/* Per tier, so it is unambiguous that FREE packs DO carry
                          the tag and both paid durations do not. */}
                      <div className="premium-tier__row premium-tier__row--tag">
                        <span className="premium-tier__detail">
                          <span className={plan === 'FREE' ? 'premium-tag--on' : 'premium-tag--off'}>
                            {plan === 'FREE' ? t('premium.tagIncluded') : t('premium.tagNone')}
                          </span>
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </Card>
          </>
        )}
      </div>
    </div>
  );
}
